import React from 'react'
import { Box, ButtonBase, Tooltip } from '@mui/material'

import { dsColorNew } from '@palette'
import { marketTone, pct, points, tone } from '@utils/colourScale'
import { cellBg, cellInk } from 'Pages/BusinessLogistics/Dashboards/OfficeNetwork/Page/@components/DistanceCell'


const REASON = {
	no_decision: ['No pass decided this cell in the window', 'no decision'],
	few: ['Fewer than 2 competitors with a price', '<2 comp.'],
	no_tier: ['The strategy compares against no tier', 'no tier'],
	incomparable: ['A price of yours is missing at the start or the end', 'n/a'],
}

const empty = (key, aria) => {
	const [long, short] = REASON[key]
	return <Box title={long} aria-label={`${aria}: ${long}`} sx={{ minWidth: 76, minHeight: 44, px: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: dsColorNew.textSubtle, textAlign: 'center', lineHeight: 1.2 }}>{short}</Box>
}

/**
 * One cell of the map (UX: `DistanceCell` with two sub-rows per ACRISS): `kind` = `market` (the tier's net change,
 * with the competitors behind it) or `engine` (your change, with Δ vs the market in points under). BI colours:
 * green ▼ − down, red ▲ + up, ≈ within ±2 %; never colour alone. "!" when fewer than 3 passes; "SIM" on a cell
 * decided in simulation. A button: Enter or a click opens the cell detail; `pressed` marks the open one.
 */
export default function ChangeCell({ cell, kind, aria, pressed = false, onOpen }) {
	if (!cell || cell.status === 'no_decision') return empty('no_decision', aria)
	const market = kind === 'market'
	if (market && cell.market_change == null) return empty(cell.n_comp_min != null && cell.n_comp_min < 2 ? 'few' : 'no_tier', aria)
	if (!market && cell.status === 'incomparable') return empty('incomparable', aria)

	const ratio = market ? cell.market_change : cell.own_change
	const t = market ? marketTone(ratio) : tone(ratio)
	const top = `${t?.arrow || ''} ${pct(ratio)}`
	const sub = market
		? `${cell.n_comp_min ?? '—'} comp.`
		: (cell.delta_pt == null ? 'no market' : `Δ ${points(cell.delta_pt)}`)
	const label = `${aria}, ${market ? 'market' : 'engine'}: ${top}${!market && cell.delta_pt != null ? `, ${points(cell.delta_pt)} vs market` : ''}${cell.thin ? ', few passes' : ''}${cell.sim ? ', simulated' : ''}. Open details`

	const button = <ButtonBase aria-label={label} aria-pressed={pressed} onClick={onOpen} sx={{
		width: '100%', minWidth: 76, minHeight: 44, px: 0.5, py: 0.5, borderRadius: '8px', position: 'relative',
		display: 'flex', flexDirection: 'column', gap: '1px', backgroundColor: market ? 'transparent' : cellBg(t), opacity: cell.thin && !market ? 0.7 : 1,
		boxShadow: pressed ? `inset 0 0 0 2px ${dsColorNew.appBlue}` : 'none',
		'&:hover': { outline: `1px solid ${dsColorNew.appBlue}` }, '&.Mui-focusVisible': { outline: `2px solid ${dsColorNew.appBlue}` },
	}}>
		<Box component="span" sx={{ fontSize: 13, fontWeight: 700, color: cellInk(t), fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{top}</Box>
		<Box component="span" sx={{ fontSize: 10.5, fontWeight: 500, color: dsColorNew.textMuted, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{sub}</Box>
		{!market && cell.thin && <Box component="span" sx={{ position: 'absolute', top: 2, right: 4, fontSize: 11, fontWeight: 700, color: dsColorNew.noteAmber }}>!</Box>}
		{!market && cell.sim && <Box component="span" sx={{ position: 'absolute', top: 2, left: 4, fontSize: 9, fontWeight: 700, color: dsColorNew.noteInfo }}>SIM</Box>}
	</ButtonBase>
	if (!cell.thin || market) return button
	return <Tooltip title={`Few passes behind this cell: ${cell.n_passes} (3 or more read better)`}>{button}</Tooltip>
}
